import { AdminRole, Participant, Tournament, TournamentAdmin, User } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { AdBanner, PatternBackground } from '@/src/presentation/components';
import { FontAwesome5, Ionicons } from '@expo/vector-icons';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { AddAdminModal, AddParticipantModal, InviteUserModal } from './components';
import { AdminsTab, ParticipantsTab, RulesTab, SettingsTab } from './tabs';
import { createStyles } from './TournamentDetailScreen.styles';

export type TabKey = 'participants' | 'admins' | 'settings' | 'rules';

export interface TournamentDetailScreenProps {
  tournament: Tournament;
  participants: Participant[];
  champion: Participant | null;
  admins: TournamentAdmin[];
  participantName: string;
  setParticipantName: (name: string) => void;
  adding: boolean;
  generating: boolean;
  isSetup: boolean;
  isValidCount: boolean;
  isChallengeTournament?: boolean;
  isLeagueTournament?: boolean;
  canManageParticipants: boolean;
  canEditTournament: boolean;
  canManageAdmins: boolean;
  userRole?: string;
  activeTab: TabKey;
  setActiveTab: (tab: TabKey) => void;

  // Admin modal
  addAdminModalVisible: boolean;
  setAddAdminModalVisible: (visible: boolean) => void;
  searchEmail: string;
  setSearchEmail: (email: string) => void;
  selectedRole: AdminRole;
  setSelectedRole: (role: AdminRole) => void;
  searchingUser: boolean;

  // Participant modal
  addParticipantModalVisible: boolean;
  setAddParticipantModalVisible: (visible: boolean) => void;
  participantSearchQuery: string;
  setParticipantSearchQuery: (query: string) => void;
  participantSearchResults: User[];
  selectedParticipantUser: User | null;
  onSelectParticipantUser: (user: User | null) => void;
  searchingParticipant: boolean;

  // Rules
  rulesText: string;
  setRulesText: (text: string) => void;
  editingRules: boolean;
  setEditingRules: (editing: boolean) => void;

  // Location
  editingLocation: boolean;
  setEditingLocation: (editing: boolean) => void;
  locationName: string;
  setLocationName: (name: string) => void;
  selectedLocation: { latitude: number; longitude: number } | null;
  setSelectedLocation: (location: { latitude: number; longitude: number } | null) => void;

  // Date
  editingDate: boolean;
  setEditingDate: (editing: boolean) => void;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  showDatePicker: boolean;

  // Invite modal
  inviteModalVisible: boolean;
  setInviteModalVisible: (visible: boolean) => void;
  inviteEmail: string;
  setInviteEmail: (email: string) => void;
  inviteMessage: string;
  setInviteMessage: (message: string) => void;
  sendingInvite: boolean;

  // Invite code
  inviteCode?: string;

  // Handlers
  onAddParticipant: () => void;
  onAddRegisteredParticipant: () => void;
  onDeleteParticipant: (participantId: string) => void;
  onParticipantPress: (userId: string) => void;
  onStartTournament: () => void;
  onViewBracket: () => void;
  onAddAdmin: () => void;
  onRemoveAdmin: (admin: TournamentAdmin) => void;
  onSaveRules: () => void;
  onSaveLocation: () => void;
  onSaveDate: () => void;
  onTogglePublic: (value: boolean) => void;
  onCopyLink: () => void;
  onShareInvite: () => void;
  onEditTournament: () => void;
  onOpenMaps: () => void;
  onGenerateInviteCode: () => void;
  onCopyInviteCode: () => void;
  onShareInviteCode: () => void;
  onSendInvitation: () => void;

  colors: any;
}

export const TournamentDetailScreen = (props: TournamentDetailScreenProps) => {
  const {
    tournament,
    participants,
    champion,
    admins,
    participantName,
    setParticipantName,
    adding,
    generating,
    isSetup,
    isValidCount,
    isChallengeTournament,
    isLeagueTournament,
    canManageParticipants,
    canEditTournament,
    canManageAdmins,
    userRole,
    activeTab,
    setActiveTab,
    addAdminModalVisible,
    setAddAdminModalVisible,
    searchEmail,
    setSearchEmail,
    selectedRole,
    setSelectedRole,
    searchingUser,
    addParticipantModalVisible,
    setAddParticipantModalVisible,
    participantSearchQuery,
    setParticipantSearchQuery,
    participantSearchResults,
    selectedParticipantUser,
    onSelectParticipantUser,
    searchingParticipant,
    rulesText,
    setRulesText,
    editingRules,
    setEditingRules,
    editingLocation,
    setEditingLocation,
    locationName,
    setLocationName,
    selectedLocation,
    setSelectedLocation,
    editingDate,
    setEditingDate,
    selectedDate,
    setSelectedDate,
    showDatePicker,
    inviteModalVisible,
    setInviteModalVisible,
    inviteEmail,
    setInviteEmail,
    inviteMessage,
    setInviteMessage,
    sendingInvite,
    inviteCode,
    onAddParticipant,
    onAddRegisteredParticipant,
    onDeleteParticipant,
    onParticipantPress,
    onStartTournament,
    onViewBracket,
    onAddAdmin,
    onRemoveAdmin,
    onSaveRules,
    onSaveLocation,
    onSaveDate,
    onTogglePublic,
    onCopyLink,
    onShareInvite,
    onEditTournament,
    onOpenMaps,
    onGenerateInviteCode,
    onCopyInviteCode,
    onShareInviteCode,
    onSendInvitation,
    colors,
  } = props;

  const styles = createStyles();
  const { t } = useTranslation();
  return (
    <>
      <PatternBackground>
        {/* Header */}
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <Text style={[styles.title, { color: colors.text }]} numberOfLines={2} ellipsizeMode="tail">{tournament.name}</Text>

          {tournament.status === 'completed' && champion && (
            <View style={[styles.championBanner, { borderColor: 'rgba(184, 134, 11, 0.3)', backgroundColor: 'rgba(184, 134, 11, 0.08)' }]}>
              <FontAwesome5 name="trophy" size={12} color="#B8860B" style={{ marginRight: 6 }} />
              <Text style={styles.championLabel}>{t('tournaments.champion').toUpperCase()}  </Text>
              <Text style={[styles.championName, { color: colors.text }]}>{champion.name}</Text>
            </View>
          )}

          {userRole && userRole !== 'owner' && (
            <View
              style={[
                styles.roleBadge,
                { backgroundColor: userRole === 'admin' ? colors.primary : colors.textTertiary },
              ]}
            >
              <Ionicons
                name={userRole === 'admin' ? 'shield-checkmark' : 'eye'}
                size={14}
                color="#fff"
                style={{ marginRight: 4 }}
              />
              <Text style={styles.roleBadgeText}>{userRole === 'admin' ? t('tournaments.admin') : t('tournaments.viewer')}</Text>
            </View>
          )}

          <Text style={[styles.participantCount, { color: colors.textSecondary }]}>
            {participants.length}
            {tournament.maxParticipants ? ` / ${tournament.maxParticipants}` : ''} {participants.length === 1 ? t('tournaments.participant') : t('tournaments.participants').toLowerCase()}
          </Text>

          {/* Location & Date Info */}
          {(tournament.location || tournament.startDate) && (
            <View style={[styles.eventInfoSection, { borderTopColor: colors.border }]}>
              {tournament.startDate && (
                <View style={styles.eventInfoRow}>
                  <Ionicons
                    name="calendar-outline"
                    size={20}
                    color={colors.textSecondary}
                    style={styles.eventInfoIcon}
                  />
                  <View style={styles.eventInfoText}>
                    <Text style={[styles.eventInfoLabel, { color: colors.textSecondary }]}>{t('tournaments.dateTime')}</Text>
                    <Text style={[styles.eventInfoValue, { color: colors.text }]}>
                      {new Date(tournament.startDate).toLocaleString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Text>
                  </View>
                </View>
              )}

              {tournament.location && (
                <TouchableOpacity
                  style={styles.eventInfoRow}
                  onPress={tournament.locationLat && tournament.locationLng ? onOpenMaps : undefined}
                  activeOpacity={tournament.locationLat && tournament.locationLng ? 0.7 : 1}
                >
                  <Ionicons
                    name="location-outline"
                    size={20}
                    color={colors.textSecondary}
                    style={styles.eventInfoIcon}
                  />
                  <View style={styles.eventInfoText}>
                    <Text style={[styles.eventInfoLabel, { color: colors.textSecondary }]}>{t('tournaments.location')}</Text>
                    <Text
                      style={[styles.eventInfoValue, { color: tournament.locationLat ? colors.primary : colors.text }]}
                    >
                      {tournament.location}
                      {tournament.locationLat && tournament.locationLng && (
                        <Ionicons name="chevron-forward" size={16} color={colors.primary} style={{ marginLeft: 4 }} />
                      )}
                    </Text>
                  </View>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>

        {/* Tab Buttons */}
        <View style={[styles.tabBar, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <TouchableOpacity
            style={[styles.tabButton, activeTab === 'participants' && styles.tabButtonActive]}
            onPress={() => setActiveTab('participants')}
          >
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={[
                styles.tabButtonText,
                { color: activeTab === 'participants' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('tournaments.participants')}
            </Text>
            {activeTab === 'participants' && (
              <View style={[styles.tabIndicator, { backgroundColor: colors.primary }]} />
            )}
          </TouchableOpacity>

          {!isChallengeTournament && (
            <TouchableOpacity
              style={[styles.tabButton, activeTab === 'admins' && styles.tabButtonActive]}
              onPress={() => setActiveTab('admins')}
            >
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={[styles.tabButtonText, { color: activeTab === 'admins' ? colors.primary : colors.textSecondary }]}
              >
                {t('tournaments.admins')}
              </Text>
              {activeTab === 'admins' && <View style={[styles.tabIndicator, { backgroundColor: colors.primary }]} />}
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={[styles.tabButton, activeTab === 'settings' && styles.tabButtonActive]}
            onPress={() => setActiveTab('settings')}
          >
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={[
                styles.tabButtonText,
                { color: activeTab === 'settings' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('tournaments.settings')}
            </Text>
            {activeTab === 'settings' && <View style={[styles.tabIndicator, { backgroundColor: colors.primary }]} />}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.tabButton, activeTab === 'rules' && styles.tabButtonActive]}
            onPress={() => setActiveTab('rules')}
          >
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={[styles.tabButtonText, { color: activeTab === 'rules' ? colors.primary : colors.textSecondary }]}
            >
              {t('tournaments.rules')}
            </Text>
            {activeTab === 'rules' && <View style={[styles.tabIndicator, { backgroundColor: colors.primary }]} />}
          </TouchableOpacity>
        </View>

        {/* Tab Content */}
        {activeTab === 'participants' && (
          <ParticipantsTab
            participants={participants}
            participantName={participantName}
            setParticipantName={setParticipantName}
            adding={adding}
            isSetup={isSetup}
            canManageParticipants={canManageParticipants}
            isValidCount={isValidCount}
            generating={generating}
            isChallengeTournament={isChallengeTournament}
            isLeagueTournament={isLeagueTournament}
            onAddParticipant={onAddParticipant}
            onAddRegisteredParticipant={() => setAddParticipantModalVisible(true)}
            onDeleteParticipant={onDeleteParticipant}
            onParticipantPress={onParticipantPress}
            onStartTournament={onStartTournament}
            onViewBracket={onViewBracket}
            colors={colors}
          />
        )}

        {activeTab === 'admins' && (
          <AdminsTab
            admins={admins}
            canManageAdmins={canManageAdmins}
            onAddAdmin={() => setAddAdminModalVisible(true)}
            onRemoveAdmin={onRemoveAdmin}
            colors={colors}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            tournament={tournament}
            userRole={userRole}
            canEditTournament={canEditTournament}
            canManageParticipants={canManageParticipants}
            isSetup={isSetup}
            inviteCode={inviteCode}
            editingLocation={editingLocation}
            setEditingLocation={setEditingLocation}
            locationName={locationName}
            setLocationName={setLocationName}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            editingDate={editingDate}
            setEditingDate={setEditingDate}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            showDatePicker={showDatePicker}
            onSaveLocation={onSaveLocation}
            onCancelLocation={() => {
              setEditingLocation(false);
              setLocationName(tournament?.location || '');
              if (tournament?.locationLat && tournament?.locationLng) {
                setSelectedLocation({
                  latitude: tournament.locationLat,
                  longitude: tournament.locationLng,
                });
              }
            }}
            onSaveDate={onSaveDate}
            onCancelDate={() => {
              setEditingDate(false);
              setSelectedDate(tournament?.startDate || new Date());
            }}
            onTogglePublic={onTogglePublic}
            onCopyLink={onCopyLink}
            onShareInvite={onShareInvite}
            onEditTournament={onEditTournament}
            onOpenMaps={onOpenMaps}
            onGenerateInviteCode={onGenerateInviteCode}
            onCopyInviteCode={onCopyInviteCode}
            onShareInviteCode={onShareInviteCode}
            onInviteByEmail={() => setInviteModalVisible(true)}
            colors={colors}
          />
        )}

        {activeTab === 'rules' && (
          <RulesTab
            rules={tournament.rules}
            rulesText={rulesText}
            setRulesText={setRulesText}
            editingRules={editingRules}
            setEditingRules={setEditingRules}
            canManageParticipants={canManageParticipants}
            onSaveRules={onSaveRules}
            onCancelRules={() => {
              setEditingRules(false);
              setRulesText(tournament?.rules || '');
            }}
            colors={colors}
          />
        )}

        <AdBanner colors={colors} />
      </PatternBackground>

      {/* Modals */}
      <AddAdminModal
        visible={addAdminModalVisible}
        searchEmail={searchEmail}
        setSearchEmail={setSearchEmail}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        searching={searchingUser}
        onAdd={onAddAdmin}
        onCancel={() => {
          setAddAdminModalVisible(false);
          setSearchEmail('');
          setSelectedRole('admin');
        }}
        colors={colors}
      />

      <AddParticipantModal
        visible={addParticipantModalVisible}
        searchQuery={participantSearchQuery}
        setSearchQuery={setParticipantSearchQuery}
        searching={searchingParticipant}
        searchResults={participantSearchResults}
        selectedUser={selectedParticipantUser}
        onSelectUser={onSelectParticipantUser}
        onAdd={onAddRegisteredParticipant}
        onCancel={() => {
          setAddParticipantModalVisible(false);
          setParticipantSearchQuery('');
          onSelectParticipantUser(null);
        }}
        colors={colors}
      />

      <InviteUserModal
        visible={inviteModalVisible}
        searchEmail={inviteEmail}
        setSearchEmail={setInviteEmail}
        message={inviteMessage}
        setMessage={setInviteMessage}
        searching={sendingInvite}
        onSend={onSendInvitation}
        onCancel={() => {
          setInviteModalVisible(false);
          setInviteEmail('');
          setInviteMessage('');
        }}
        colors={colors}
      />
    </>
  );
};

export const TournamentDetailScreenLoading = ({ colors }: { colors: { background: string; primary: string } }) => {
  const styles = createStyles();
  return (
    <View style={[styles.centerContainer, { backgroundColor: colors.background }]}>
      <ActivityIndicator size="large" color={colors.primary} />
    </View>
  );
};

export const TournamentDetailScreenError = ({
  colors,
  message,
}: {
  colors: { background: string; error: string };
  message: string;
}) => {
  const styles = createStyles();
  return (
    <View style={[styles.centerContainer, { backgroundColor: colors.background }]}>
      <Text style={[styles.errorText, { color: colors.error }]}>{message}</Text>
    </View>
  );
};
